import { useMemo, useState } from 'react';
import { Button } from '../components/ui.jsx';

/**
 * The date range every report is read through.
 *
 * The API has always taken from and to (routes/reports.js), it was only the screen that
 * never asked, so every figure came back as all time. This is the missing control rather
 * than a new capability.
 *
 * Dates are computed in UTC on purpose. sold_at is stored as a UTC timestamp and Ghana is
 * UTC+0 all year with no daylight saving, so the UTC day and the shop day are the same day.
 * Deriving "yesterday" from the viewer's clock instead would quietly shift the boundary for
 * anyone reading the books from another country, and the takings for a day would change
 * depending on where you opened the page.
 */

const pad = n => String(n).padStart(2, '0');
const iso = d => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const addDays = (d, n) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "26 Sep 2026" from a YYYY-MM-DD string, without going through Date and its timezones. */
function pretty(day) {
  if (!day) return '';
  const [y, m, d] = day.split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

export const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'all', label: 'All time' }
];

function resolve(preset, custom) {
  const now = new Date();

  if (preset === 'custom') {
    // A half filled custom range is still a valid question: "everything since the 3rd".
    return { from: custom.from || null, to: custom.to || null };
  }
  if (preset === 'today') return { from: iso(now), to: iso(now) };
  if (preset === 'yesterday') {
    const y = iso(addDays(now, -1));
    return { from: y, to: y };
  }
  if (preset === 'week') {
    // Monday, because a salon week is not a calendar week starting Sunday.
    const dow = (now.getUTCDay() + 6) % 7;
    return { from: iso(addDays(now, -dow)), to: iso(now) };
  }
  if (preset === 'month') {
    return { from: iso(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))), to: iso(now) };
  }
  return { from: null, to: null };
}

function describe(preset, range) {
  if (preset === 'all' || (!range.from && !range.to)) return 'All time to date';
  if (preset === 'today') return `Today, ${pretty(range.from)}`;
  if (preset === 'yesterday') return `Yesterday, ${pretty(range.from)}`;
  if (!range.from) return `Up to ${pretty(range.to)}`;
  if (!range.to) return `From ${pretty(range.from)}`;
  if (range.from === range.to) return pretty(range.from);
  return `${pretty(range.from)} to ${pretty(range.to)}`;
}

/**
 * Returns the range plus the params to hand to api.report. Keys are omitted rather than
 * sent empty, because URLSearchParams turns a null into the literal string "null" and the
 * server would then filter on it.
 */
export function useDateRange(initial = 'all') {
  const [preset, setPreset] = useState(initial);
  const [custom, setCustom] = useState({ from: '', to: '' });

  const range = useMemo(() => resolve(preset, custom), [preset, custom]);
  const params = useMemo(() => {
    const p = {};
    if (range.from) p.from = range.from;
    if (range.to) p.to = range.to;
    return p;
  }, [range]);

  const label = useMemo(() => describe(preset, range), [preset, range]);

  // Editing either date field is itself the act of choosing a custom range.
  const edit = patch => { setCustom(c => ({ ...c, ...patch })); setPreset('custom'); };

  return { preset, setPreset, custom, edit, range, params, label };
}

export function DateRangeBar({ preset, setPreset, custom, edit, label }) {
  return (
    <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {PRESETS.map(p => (
        <Button key={p.key} size="m" variant={preset === p.key ? 'primary' : 'secondary'}
          onClick={() => setPreset(p.key)}>
          {p.label}
        </Button>
      ))}

      <span style={{ width: 1, height: 24, background: 'var(--color-border-default)', margin: '0 4px' }} />

      <input type="date" value={custom.from} max={custom.to || undefined} aria-label="From"
        onChange={e => edit({ from: e.target.value })} style={dateInput} />
      <span className="t-body-s muted">to</span>
      <input type="date" value={custom.to} min={custom.from || undefined} aria-label="To"
        onChange={e => edit({ to: e.target.value })} style={dateInput} />

      <span style={{ flex: 1 }} />
      <span className="t-body-s muted">{label}</span>
    </div>
  );
}

const dateInput = {
  padding: '7px 10px',
  border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-bg-surface)',
  color: 'var(--color-text-primary)',
  font: 'inherit'
};
